const MESES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

const soles = (n) => `S/ ${Number(n || 0).toLocaleString('es-PE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})}`;

export function formatPeriodoLabel(filtros = {}) {
  if (filtros.fechaDesde || filtros.fechaHasta) {
    const desde = filtros.fechaDesde
      ? new Date(filtros.fechaDesde).toLocaleDateString('es-PE')
      : 'inicio';
    const hasta = filtros.fechaHasta
      ? new Date(filtros.fechaHasta).toLocaleDateString('es-PE')
      : 'hoy';
    return `${desde} al ${hasta}`;
  }

  let meses = filtros.meses;
  if (typeof meses === 'string' && meses.length) {
    meses = meses.split(',').map((m) => parseInt(m.trim(), 10)).filter((n) => n >= 1 && n <= 12);
  }
  if (!Array.isArray(meses)) meses = [];
  meses = meses.map((m) => parseInt(m, 10)).filter((n) => n >= 1 && n <= 12);

  const anio = filtros.anio || String(new Date().getFullYear());

  if (meses.length === 1) {
    return `${MESES_ES[meses[0] - 1]} de ${anio}`;
  }
  if (meses.length > 1) {
    const nombres = meses.map((m) => MESES_ES[m - 1]).join(', ');
    return `${nombres} de ${anio}`;
  }
  return `Año ${anio}`;
}

function buildPulse({
  conAlertaCobranza,
  saldoEnRiesgo,
  saldoTotal,
  resumen,
  entregados,
  total,
  pctCobradoGlobal
}) {
  const cobranzaEstado = conAlertaCobranza.length > 0
    ? 'warning'
    : (pctCobradoGlobal >= 80 ? 'ok' : 'neutral');

  const plazosEstado = resumen.vencidos > 0
    ? 'danger'
    : (resumen.peorDemora?.tonoDemora === 'warning' ? 'warning' : 'ok');

  return [
    {
      id: 'cobranza',
      estado: cobranzaEstado,
      titulo: 'Cobranza',
      detalle: conAlertaCobranza.length > 0
        ? `${conAlertaCobranza.length} a seguir · ${soles(saldoEnRiesgo)}`
        : (saldoTotal > 0 ? 'Saldos en plazo' : 'Al día')
    },
    {
      id: 'plazos',
      estado: plazosEstado,
      titulo: 'Plazos',
      detalle: resumen.vencidos > 0
        ? `${resumen.vencidos} vencido${resumen.vencidos === 1 ? '' : 's'}`
        : 'Sin vencidos'
    },
    {
      id: 'entregas',
      estado: entregados === total && total > 0 ? 'ok' : 'neutral',
      titulo: 'Entregas',
      detalle: total > 0 ? `${entregados} de ${total} cerrados` : '—'
    }
  ];
}

function buildAtencion(datosEnriquecidos) {
  const items = [];

  for (const e of [...datosEnriquecidos].filter((x) => x.alertaCobranza).sort((a, b) => b.adeudado - a.adeudado)) {
    items.push({
      tipo: 'cobranza',
      prioridad: e.estadoCobranza === 'danger' ? 1 : 2,
      expediente: e.numero,
      cliente: e.cliente.nombre,
      monto: e.adeudado,
      detalle: e.etiquetaCobranza
    });
  }

  for (const e of [...datosEnriquecidos].filter((x) => !x.entregado && x.tonoDemora === 'danger' && x.diasRetraso > 0)) {
    if (items.some((i) => i.expediente === e.numero)) continue;
    items.push({
      tipo: 'plazo',
      prioridad: 1,
      expediente: e.numero,
      cliente: e.cliente.nombre,
      monto: e.adeudado,
      detalle: `Vencido hace ${e.diasRetraso} d — avanzar partes`
    });
  }

  for (const e of [...datosEnriquecidos].filter((x) => !x.entregado && x.tonoDemora === 'warning' && !x.alertaCobranza)) {
    if (items.some((i) => i.expediente === e.numero)) continue;
    items.push({
      tipo: 'plazo',
      prioridad: 2,
      expediente: e.numero,
      cliente: e.cliente.nombre,
      monto: null,
      detalle: e.etiquetaDemora
    });
  }

  return items.sort((a, b) => a.prioridad - b.prioridad).slice(0, 5);
}

function buildEnOrden(datosEnriquecidos) {
  return [...datosEnriquecidos]
    .filter((e) => e.adeudado > 0 && !e.alertaCobranza)
    .sort((a, b) => b.adeudado - a.adeudado)
    .slice(0, 4)
    .map((e) => ({
      expediente: e.numero,
      cliente: e.cliente.nombre,
      saldo: e.adeudado,
      detalle: e.etiquetaCobranza
    }));
}

function buildDestacados({ topR, topC, mejorServicio, total }) {
  if (total === 0) return [];
  const items = [];
  if (topR) {
    items.push({
      id: 'rentable',
      texto: `Mejor proyecto: **${topR.numero}** · ${soles(topR.montoAPagar)}`
    });
  }
  if (topC) {
    items.push({
      id: 'cliente',
      texto: `Cliente destacado: **${topC.cliente.nombre}**`
    });
  }
  if (mejorServicio && items.length < 3) {
    items.push({
      id: 'servicio',
      texto: `Servicio líder: **${mejorServicio.tipo}**`
    });
  }
  return items.slice(0, 3);
}

function buildVeredicto({
  total,
  periodo,
  conAlertaCobranza,
  saldoEnRiesgo,
  resumen,
  entregados
}) {
  if (total === 0) {
    return `No hay expedientes en ${periodo}. Ajusta el filtro del dashboard para generar el informe.`;
  }

  const partes = [`**${total} proyecto${total === 1 ? '' : 's'}** en ${periodo}.`];

  if (conAlertaCobranza.length > 0) {
    partes.push(
      `**${conAlertaCobranza.length} cobro${conAlertaCobranza.length === 1 ? '' : 's'}** `
      + `requieren seguimiento (${soles(saldoEnRiesgo)}).`
    );
  } else if (resumen.vencidos === 0) {
    partes.push('La cartera va **en plazo** y sin alertas de cobranza.');
  }

  if (resumen.vencidos > 0) {
    partes.push(`**${resumen.vencidos} expediente${resumen.vencidos === 1 ? '' : 's'} vencido${resumen.vencidos === 1 ? '' : 's'}** sin cerrar.`);
  } else if (entregados === 0 && total > 0) {
    partes.push('Aún no hay entregas cerradas — normal si los proyectos acaban de iniciar.');
  }

  return partes.join(' ');
}

/** Acción recomendada según plazo + cobranza (no siempre es solo "cobrar"). */
function accionParaExpediente(exp) {
  if (!exp) return null;

  const ref = `**${exp.numero}** (${exp.cliente.nombre})`;

  if (exp.entregado && exp.adeudado > 0) {
    return `Cobrar saldo final: ${ref} — ${soles(exp.adeudado)} pendiente tras entrega.`;
  }

  if (!exp.entregado && exp.tonoDemora === 'danger') {
    const cobro = exp.pctCobrado < 80 ? `, ${exp.pctCobrado}% cobrado` : '';
    return `Priorizar avance: ${ref} — vencido hace ${exp.diasRetraso} d${cobro}. Revisar partes y asignación.`;
  }

  if (!exp.entregado && exp.tonoDemora === 'warning') {
    return `Priorizar cierre: ${ref} — ${exp.etiquetaDemora}, ${exp.pctCobrado}% cobrado. Avanzar partes y gestionar saldo ${soles(exp.adeudado)}.`;
  }

  if (exp.pctCobrado < 15) {
    return `Cobrar adelanto: ${ref} — ${soles(exp.adeudado)} sin abonos. ${exp.etiquetaCobranza}.`;
  }

  return `Gestionar cobro: ${ref} — ${soles(exp.adeudado)}. ${exp.etiquetaCobranza}.`;
}

function expedientePrioridadAccion(expedientes) {
  const candidatos = expedientes.filter(
    (e) => e.alertaCobranza || (!e.entregado && e.tonoDemora !== 'ok')
  );

  const score = (e) => {
    if (!e.entregado && e.tonoDemora === 'danger') return 1000 + e.diasRetraso;
    if (!e.entregado && e.tonoDemora === 'warning') {
      const diasRest = Math.max(0, e.diasPrometidos - e.diasTranscurridos);
      return 600 - diasRest;
    }
    if (e.alertaCobranza) return 200 + e.adeudado;
    return 0;
  };

  return [...candidatos].sort((a, b) => score(b) - score(a))[0] || null;
}

function buildConclusion({
  total,
  conAlertaCobranza,
  resumen,
  datosEnriquecidos,
  mejorServicio
}) {
  if (total === 0) {
    return ['Amplía filtros o cambia el mes para ver datos.'];
  }

  const items = [];
  const prioridad = expedientePrioridadAccion(datosEnriquecidos);

  if (prioridad) {
    items.push(accionParaExpediente(prioridad));
  }

  const otrosVencidos = datosEnriquecidos.filter(
    (e) => !e.entregado && e.tonoDemora === 'danger' && e.numero !== prioridad?.numero
  );
  if (resumen.vencidos > 1 || (resumen.vencidos === 1 && prioridad?.tonoDemora !== 'danger')) {
    items.push(
      `Operación: avanzar **${resumen.vencidos} vencido${resumen.vencidos === 1 ? '' : 's'}** para recuperar plazos.`
    );
  } else if (otrosVencidos.length > 0) {
    items.push(`Operación: revisar también **${otrosVencidos[0].numero}** y expedientes vencidos restantes.`);
  }

  const soloCobro = datosEnriquecidos.filter(
    (e) => e.alertaCobranza && e.tonoDemora === 'ok' && e.numero !== prioridad?.numero
  );
  if (soloCobro.length > 0 && items.length < 3) {
    const e = soloCobro.sort((a, b) => b.adeudado - a.adeudado)[0];
    items.push(accionParaExpediente(e));
  }

  if (conAlertaCobranza.length === 0 && resumen.vencidos === 0 && mejorServicio) {
    items.push(`Comercial: **${mejorServicio.tipo}** fue el servicio más fuerte del periodo.`);
  }

  if (items.length === 0) {
    items.push('Sin acciones urgentes. Revisar el análisis detallado si necesitas scores y tablas.');
  }

  return items.slice(0, 3);
}

/** Tablas compactas para el análisis detallado (nivel 2), no para el informe ejecutivo */
function tablasDetalle(analisis) {
  const { datosEnriquecidos, rentabilidadExpedientes, demorasExpedientes, clientesRanking } = analisis;

  const cobranza = [...datosEnriquecidos]
    .filter((e) => e.adeudado > 0)
    .sort((a, b) => (b.alertaCobranza - a.alertaCobranza) || (b.adeudado - a.adeudado))
    .slice(0, 5)
    .map((e) => ({
      expediente: e.numero,
      cliente: e.cliente.nombre,
      saldo: e.adeudado,
      alerta: e.alertaCobranza,
      lectura: e.etiquetaCobranza
    }));

  const demoras = [...demorasExpedientes]
    .filter((e) => e.diasRetraso > 0 || e.tonoDemora === 'danger')
    .slice(0, 5)
    .map((e) => ({
      expediente: e.numero,
      cliente: e.cliente.nombre,
      retraso: e.diasRetraso > 0 ? `+${e.diasRetraso} d` : e.etiquetaDemora,
      lectura: e.etiquetaDemora
    }));

  const rentables = rentabilidadExpedientes.slice(0, 5).map((e) => ({
    expediente: e.numero,
    cliente: e.cliente.nombre,
    vendido: e.montoAPagar,
    score: e.scoreRentabilidad,
    lectura: e.interpretacionRentabilidad
  }));

  const clientes = clientesRanking.slice(0, 5).map((c) => ({
    cliente: c.cliente.nombre,
    expedientes: c.cantidad,
    cobrado: c.cobrado,
    valor: c.scoreValor,
    lectura: c.interpretacionCliente
  }));

  return { cobranza, demoras, rentables, clientes };
}

export function generarInformeNarrativo(analisis, filtros = {}) {
  const {
    total,
    resumen,
    rentabilidadPorServicio,
    datosEnriquecidos
  } = analisis;

  const periodo = formatPeriodoLabel(filtros);
  const vendido = datosEnriquecidos.reduce((s, e) => s + (e.montoAPagar || 0), 0);
  const cobrado = datosEnriquecidos.reduce((s, e) => s + (e.totalAbonado || 0), 0);
  const saldoTotal = datosEnriquecidos.reduce((s, e) => s + (e.adeudado || 0), 0);
  const entregados = datosEnriquecidos.filter((e) => e.entregado).length;
  const pagados = datosEnriquecidos.filter((e) => e.estadoPagoAdmin === 'PAGADO').length;

  const mejorServicio = rentabilidadPorServicio[0] || null;
  const topR = resumen.topRentable;
  const topD = resumen.peorDemora;
  const topC = resumen.mejorCliente;

  const conAlertaCobranza = datosEnriquecidos.filter((e) => e.alertaCobranza);
  const saldoEnRiesgo = conAlertaCobranza.reduce((s, e) => s + (e.adeudado || 0), 0);

  const veredicto = buildVeredicto({
    total,
    periodo,
    conAlertaCobranza,
    saldoEnRiesgo,
    resumen,
    entregados
  });

  const pulse = buildPulse({
    conAlertaCobranza,
    saldoEnRiesgo,
    saldoTotal,
    resumen,
    entregados,
    total,
    pctCobradoGlobal: resumen.pctCobradoGlobal
  });

  const atencion = buildAtencion(datosEnriquecidos);
  const enOrden = buildEnOrden(datosEnriquecidos);
  const destacados = buildDestacados({ topR, topC, mejorServicio, total });
  const conclusion = buildConclusion({
    total,
    conAlertaCobranza,
    resumen,
    datosEnriquecidos,
    mejorServicio
  });

  const detalle = tablasDetalle(analisis);

  return {
    periodo,
    fechaGeneracion: new Date().toLocaleString('es-PE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    total,
    vendido,
    cobrado,
    saldoTotal,
    pctCobrado: resumen.pctCobradoGlobal,
    veredicto,
    resumenEjecutivo: veredicto,
    pulse,
    atencion,
    enOrden,
    destacados,
    conclusion,
    secciones: [],
    tablasDetalle: detalle,
    statsGraficos: {
      pagados,
      pendientes: total - pagados,
      entregados,
      enCurso: total - entregados,
      porServicio: rentabilidadPorServicio.slice(0, 6).map((g) => ({
        nombre: g.tipo,
        vendido: g.vendido,
        cobrado: g.cobrado,
        pctCobrado: g.pctCobrado
      }))
    }
  };
}
